import React from 'react'
import { FC } from 'react'
import { css } from '@emotion/react'
import { Link } from 'gatsby'
import LogoFooter from '@images/LogoFooter'
import { MyTheme, theme } from '@ui/styles/theme'
import { useTranslation } from 'react-i18next'
import { buildLangPath } from '@lib/pathUtils'
import { Languages } from '@/types/languages'

type Props = {
  lang: Languages
}

const Footer: FC<Props> = ({ lang }) => {
  const styles = createStyles(theme)
  const { t } = useTranslation()

  const contactUs =
    lang === 'ja'
      ? 'https://docs.google.com/forms/d/e/1FAIpQLSdAny1Z3ptdIgVrTc4o2JTg-SIYf2rLGzvfoGhNA7-Fwk4mug/viewform'
      : 'https://docs.google.com/forms/d/1CFD7H7JjVB9kV902VUj3p9OeRaDHCiX89dkLIx8WMrU/viewform'

  const currentYear = new Date().getFullYear()

  return (
    <footer css={styles.footer}>
      <div css={styles.footerContainer}>
        <div css={styles.footerTop}>
          <div>
            <Link to={buildLangPath('/', lang)} css={styles.footerLogo}>
              <LogoFooter />
            </Link>
          </div>
          <div css={styles.footerLinks}>
            <a
              href={contactUs}
              target="_blank"
              rel="noreferrer"
              css={styles.footerLink}
            >
              {t('お問い合わせ')}
            </a>
            {(lang === 'ja' || lang === 'en') && (
              <Link
                to={buildLangPath('/specified-commercial-transactions', lang)}
                css={styles.footerLink}
              >
                {t('特定商取引法に基づく表記')}
              </Link>
            )}
          </div>
        </div>
        <div css={styles.footerBottom}>
          <span css={styles.footerCopyright}>
            ©Aldagram Inc. {currentYear}
          </span>
        </div>
      </div>
    </footer>
  )
}

const createStyles = (theme: MyTheme) => {
  return {
    footer: css`
      background: ${theme.colors.background.innermost};
      width: 100%;
      padding: 60px 32px 40px;
      ${theme.mq.isDesktop} {
        padding: 40px 32px 32px;
      }
    `,
    footerContainer: css`
      max-width: 1200px;
      margin: 0;
    `,
    footerTop: css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 32px;
      margin-bottom: 16px;
      ${theme.mq.isDesktop} {
        margin-bottom: 24px;
      }
    `,
    footerBottom: css`
      text-align: left;
      ${theme.mq.isTablet} {
        text-align: left;
      }
    `,
    footerLogo: css`
      &:hover {
        opacity: 0.7;
      }
    `,
    footerLinks: css`
      display: flex;
      flex-direction: column;
      gap: 0;
      font-size: 14px;
      width: 100%;
      ${theme.mq.isDesktop} {
        width: auto;
        flex-direction: row;
        align-items: center;
        gap: 32px;
      }
    `,
    footerLink: css`
      color: ${theme.colors.primary};
      font-size: 14px;
      line-height: 36px;
      text-decoration: none;
      &:hover {
        color: ${theme.colors.primaryDark};
      }
    `,
    footerCopyright: css`
      color: ${theme.colors.textGray};
      font-size: 11px;
    `
  }
}

export default Footer
